import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import { ChevronLeft, Bell, Eye, Shield, Trash2, AlertTriangle, X } from 'lucide-react';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, userData, updateProfile, deleteOwnAccount, logout } = useUser();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isNavigating, setIsNavigating] = useState(false);

  const isPaid = userData?.subscription === 'paid';

  // Notifications reads from the same emailOptIn field the admin dashboard uses
  const [emailOptIn, setEmailOptIn] = useState(userData?.emailOptIn ?? false);
  // Privacy reads from settings.showProfilePublic
  const [showProfilePublic, setShowProfilePublic] = useState(userData?.settings?.showProfilePublic ?? true);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Sync toggle state when userData arrives from Firestore
  useEffect(() => {
    if (!userData) return;
    setEmailOptIn(userData.emailOptIn ?? false);
    setShowProfilePublic(userData.settings?.showProfilePublic ?? true);
  }, [userData]);

  useEffect(() => {
    const timer = setTimeout(() => setIsPageLoading(false), 50);
    return () => clearTimeout(timer);
  }, []);

  // Lock body scroll when delete confirmation modal is open
  useEffect(() => {
    if (showDeleteConfirm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showDeleteConfirm]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleToggleEmailOptIn = async (value) => {
    setEmailOptIn(value);
    setIsSaving(true);
    await updateProfile({ emailOptIn: value });
    setIsSaving(false);
  };

  const handleTogglePrivacy = async (value) => {
    setShowProfilePublic(value);
    const updatedSettings = {
      ...(userData?.settings || {}),
      showProfilePublic: value,
    };
    setIsSaving(true);
    await updateProfile({ settings: updatedSettings });
    setIsSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Please enter your password.');
      return;
    }
    setIsDeleting(true);
    setDeleteError('');

    const result = await deleteOwnAccount(deletePassword);
    if (result.success) {
      navigate('/', { replace: true });
    } else {
      setDeleteError(result.error);
      setIsDeleting(false);
    }
  };

  const resetDeleteFlow = () => {
    setShowDeleteConfirm(false);
    setDeleteStep(1);
    setDeletePassword('');
    setDeleteError('');
    setIsDeleting(false);
  };

  const Toggle = ({ enabled, onToggle }) => (
    <button
      onClick={() => onToggle(!enabled)}
      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-200 ${
        enabled ? 'bg-gradient-to-r from-green-400 to-green-500' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
          enabled ? 'translate-x-7' : 'translate-x-1'
        }`}
      />
    </button>
  );

  return (
    <div className={`min-h-screen min-h-[100dvh] bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-100`}>
      {/* Top Navigation */}
      <nav className="bg-white/90 shadow-lg sticky top-0 z-20 border-b-3 sm:border-b-4 border-purple-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src="/assets/brlogo2.webp" alt="BrightReading Logo" className="h-14 w-auto object-contain" />
              <div>
                <h1 className="text-2xl font-black text-gray-900">Settings</h1>
                <p className="text-sm text-gray-600 font-semibold">Manage your preferences</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleNavigate('/profile')}
                className="bg-purple-100 hover:bg-purple-200 text-purple-600 px-5 py-3 rounded-full transition font-bold shadow-md flex items-center gap-2"
              >
                <ChevronLeft size={20} />
                Profile
              </button>
              <button
                onClick={() => handleNavigate('/dashboard')}
                className="hover:opacity-80 transition transform hover:scale-105"
              >
                <img src="/assets/HomeButton.webp" alt="Home" className="h-12 w-auto object-contain" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Notifications */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-8 border-3 sm:border-4 border-blue-200">
          <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
            <Bell className="text-blue-500" size={28} />
            Notifications
          </h3>
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-gray-900 text-lg">Email Communications</p>
                <p className="text-sm text-gray-500 font-semibold">Receive learning reminders, tips, and progress updates via email</p>
              </div>
              <Toggle enabled={emailOptIn} onToggle={handleToggleEmailOptIn} />
            </div>
          </div>
        </div>

        {/* Privacy */}
        <div className={`bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-8 border-3 sm:border-4 ${isPaid ? 'border-purple-200' : 'border-gray-200'}`}>
          <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
            <Eye className={isPaid ? 'text-purple-500' : 'text-gray-400'} size={28} />
            Privacy
          </h3>
          <div className="space-y-5">
            <div className={`flex items-center justify-between ${!isPaid ? 'opacity-50' : ''}`}>
              <div>
                <p className="font-bold text-gray-900 text-lg">Discoverable Profile</p>
                <p className="text-sm text-gray-500 font-semibold">Allow other families to find and add you by username</p>
              </div>
              {isPaid ? (
                <Toggle enabled={showProfilePublic} onToggle={handleTogglePrivacy} />
              ) : (
                <div className="relative inline-flex h-8 w-14 items-center rounded-full bg-gray-300 cursor-not-allowed">
                  <span className="inline-block h-6 w-6 transform rounded-full bg-white shadow-md translate-x-1" />
                </div>
              )}
            </div>
            {!isPaid && (
              <div className="bg-orange-50 rounded-2xl p-5 border-2 border-orange-200 flex items-start gap-3">
                <div className="text-2xl flex-shrink-0">🔒</div>
                <div>
                  <p className="font-black text-gray-800 mb-1">Premium Feature</p>
                  <p className="text-sm text-gray-600 font-semibold">
                    Community features (friends, messaging, and learning circles) require a Premium subscription. Your profile is hidden from other users until you upgrade.
                  </p>
                  <button
                    onClick={() => handleNavigate('/upgrade')}
                    className="mt-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-5 py-2 rounded-full font-bold text-sm transition hover:shadow-lg transform hover:scale-105"
                  >
                    Upgrade to Premium
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Account Info */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-8 border-3 sm:border-4 border-orange-200">
          <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
            <Shield className="text-orange-500" size={28} />
            Account
          </h3>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-2xl p-5 border-2 border-gray-200">
              <p className="text-xs font-bold text-gray-500 uppercase mb-1">Email</p>
              <p className="font-bold text-gray-900 text-lg">{user?.email}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-5 border-2 border-gray-200">
              <p className="text-xs font-bold text-gray-500 uppercase mb-1">Subscription</p>
              <p className="font-bold text-gray-900 text-lg capitalize">{userData?.subscription || 'Free'}</p>
              {userData?.subscription !== 'paid' && (
                <button
                  onClick={() => handleNavigate('/upgrade')}
                  className="mt-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-5 py-2 rounded-full font-bold text-sm transition hover:shadow-lg transform hover:scale-105"
                >
                  Upgrade to Premium
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-8 border-3 sm:border-4 border-gray-200">
          <h3 className="text-2xl font-black text-gray-900 mb-6">Legal</h3>
          <div className="space-y-3">
            <button
              onClick={() => handleNavigate('/terms')}
              className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-2xl p-5 border-2 border-gray-200 font-bold text-gray-700 transition flex items-center justify-between"
            >
              Terms of Service
              <ChevronLeft size={20} className="rotate-180 text-gray-400" />
            </button>
            <button
              onClick={() => handleNavigate('/privacy')}
              className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-2xl p-5 border-2 border-gray-200 font-bold text-gray-700 transition flex items-center justify-between"
            >
              Privacy Policy
              <ChevronLeft size={20} className="rotate-180 text-gray-400" />
            </button>
            <button
              onClick={() => handleNavigate('/contact')}
              className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-2xl p-5 border-2 border-gray-200 font-bold text-gray-700 transition flex items-center justify-between"
            >
              Contact Us
              <ChevronLeft size={20} className="rotate-180 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Danger Zone — Delete Account */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-8 border-3 sm:border-4 border-red-200">
          <h3 className="text-2xl font-black text-red-600 mb-3 flex items-center gap-3">
            <AlertTriangle className="text-red-500" size={28} />
            Danger Zone
          </h3>
          <p className="text-gray-600 font-semibold mb-6">
            Deleting your account is permanent. All progress, children data, and achievements will be lost forever.
          </p>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="bg-red-100 hover:bg-red-200 text-red-600 px-6 py-4 rounded-2xl font-black transition flex items-center gap-3 border-2 border-red-300"
          >
            <Trash2 size={20} />
            Delete My Account
          </button>
        </div>

        {isSaving && (
          <div className="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-3 rounded-full font-bold shadow-xl z-50 animate-pulse">
            Saving...
          </div>
        )}
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full border-3 sm:border-4 border-red-300 p-4 sm:p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black text-red-600 flex items-center gap-2">
                <AlertTriangle size={28} />
                {deleteStep === 1 ? 'Are you sure?' : 'Confirm Deletion'}
              </h2>
              <button
                onClick={resetDeleteFlow}
                className="bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>

            {deleteStep === 1 && (
              <>
                <div className="bg-red-50 rounded-2xl p-6 border-2 border-red-200 mb-6">
                  <p className="text-gray-800 font-semibold mb-3">This will permanently delete:</p>
                  <ul className="space-y-2 text-gray-700 font-semibold">
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full" />
                      Your account and login
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full" />
                      All learning progress and streaks
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full" />
                      Children profiles and achievements
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full" />
                      Friends and community connections
                    </li>
                  </ul>
                </div>
                <p className="text-gray-600 font-semibold mb-6 text-sm">This action cannot be undone.</p>
                <div className="flex gap-4">
                  <button
                    onClick={() => setDeleteStep(2)}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white py-4 rounded-2xl font-black transition text-lg"
                  >
                    I Understand, Continue
                  </button>
                  <button
                    onClick={resetDeleteFlow}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-4 rounded-2xl font-black transition text-lg"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {deleteStep === 2 && (
              <>
                <p className="text-gray-700 font-semibold mb-4">
                  Enter your password to confirm account deletion.
                </p>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => { setDeletePassword(e.target.value); setDeleteError(''); }}
                  className="w-full px-5 py-4 border-3 border-red-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-red-300 focus:border-red-400 text-lg font-semibold mb-4"
                  placeholder="Your password"
                  autoComplete="current-password"
                />
                {deleteError && (
                  <p className="text-red-600 text-sm font-black mb-4">{deleteError}</p>
                )}
                <div className="flex gap-4">
                  <button
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-4 rounded-2xl font-black transition text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Forever'}
                  </button>
                  <button
                    onClick={resetDeleteFlow}
                    disabled={isDeleting}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-4 rounded-2xl font-black transition text-lg disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
